import { DisplayViewport, DisplayableImageData, Size } from 'app/interfaces/Image';
import {
  BasicDisplayOptions,
  BasicImageRenderer,
  BasicImageRendererFactory,
  BasicRendererAnnotationsForDisplay,
  DisplayOptions,
} from 'app/interfaces/ImageRenderer';
import { prepareImageForCanvasDisplay } from 'app/utils/imageManipulation/ImageTransformation';
import _ from 'lodash';
import DisplayEvent from 'app/interfaces/DisplayEvent';
import { Transformation } from 'app/interfaces/Image/Transformation';
import { convertCropAnnotationsToCropRect } from 'app/utils/image/convertCropAnnotationsToCropRect';
import {
  computeScaleFactorFromStoredViewport,
  getMatchingResolutionViewport,
  rescaleAnnotationsPoints,
} from 'app/utils/cornerstone/imageUtils';
import { getDefaultViewport } from 'cornerstone-core';
import { EncodeOptions, IDisplayableImageEncoder } from 'app/interfaces/IDisplayableImageEncoder';

export class ThumbnailImageRenderer extends EventTarget implements BasicImageRenderer {
  public element: HTMLElement;
  private displayWidth: number;
  private displayHeight: number;
  private canvas: HTMLCanvasElement;
  private resizeObserver: ResizeObserver;
  private displayedImage?: DisplayableImageData;
  private lastDisplayedImage?: DisplayableImageData;
  private viewport?: DisplayViewport;
  private annotations?: BasicRendererAnnotationsForDisplay;
  private metadata?: DisplayOptions['metadata'];
  private lastTransformation?: Transformation;
  private lastEncodeOptions?: EncodeOptions;
  private displayableImageEncoder?: IDisplayableImageEncoder;

  constructor(
    element: HTMLElement,
    { displayableImageEncoder }: { displayableImageEncoder?: IDisplayableImageEncoder } = {}
  ) {
    super();
    this.element = element;
    this.displayableImageEncoder = displayableImageEncoder;

    this.element.style.width = '100%';
    this.element.style.height = '100%';
    this.element.style.display = 'flex';
    this.element.style.alignItems = 'center';
    this.element.style.justifyContent = 'center';

    this.canvas = this.element.querySelector('canvas');
    if (!this.canvas) {
      document.createElement('canvas');
      this.element.appendChild(this.canvas);
    }
    this.canvas.style.objectFit = 'none';

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.element);

    const { width, height } = element.getBoundingClientRect();
    this.displayWidth = Math.floor(width);
    this.displayHeight = Math.floor(height);

    this.dispatchEvent(new DisplayEvent('display_updated', false));
  }

  displayImage = (
    image: DisplayableImageData,
    { viewport, annotations, metadata }: BasicDisplayOptions
  ) => {
    this.annotations = annotations;
    this.metadata = metadata;
    // @ts-ignore image type from cornerstone is wrong
    this.viewport = viewport ?? getDefaultViewport(this.canvas, image);
    this.displayedImage = image;

    this.triggerCanvasDisplay();
  };

  destroy = () => {
    this.resizeObserver.disconnect();
    this.canvas.remove();
  };

  private displayWithImageTransformer = () => {
    const isImageDisplayable =
      this.displayedImage &&
      this.displayWidth &&
      this.displayHeight &&
      this.element &&
      this.viewport;

    if (!isImageDisplayable) {
      this.dispatchEvent(new DisplayEvent('display_updated', false));
      return;
    }

    const viewport = getMatchingResolutionViewport(this.viewport, this.displayedImage);
    const scaleFactor = computeScaleFactorFromStoredViewport(
      this.viewport,
      this.displayedImage
    ).scaleFactor;
    const annotations =
      scaleFactor !== 1
        ? rescaleAnnotationsPoints(this.annotations, scaleFactor)
        : this.annotations;

    const cropRect = convertCropAnnotationsToCropRect(annotations?.Crop);
    let crop;
    if (cropRect) {
      const [x, y, width, height] = cropRect;
      const imageWidth = this.displayedImage.width;
      const imageHeight = this.displayedImage.height;

      const clippedX = Math.max(0, Math.min(x, imageWidth));
      const clippedY = Math.max(0, Math.min(y, imageHeight));
      const clippedWidth = Math.max(0, Math.min(width, imageWidth - clippedX));
      const clippedHeight = Math.max(0, Math.min(height, imageHeight - clippedY));

      if (clippedWidth > 0 && clippedHeight > 0) {
        crop = { x: clippedX, y: clippedY, width: clippedWidth, height: clippedHeight };
      }
    }
    const transformations = {
      ..._.pick(viewport, ['invert', 'rotation', 'hflip', 'vflip']),
      voi: {
        windowCenter: viewport?.voi?.windowCenter,
        windowWidth: viewport?.voi?.windowWidth,
      },
      crop,
      resize: { width: this.displayWidth, height: this.displayHeight },
    };

    const isImageAlreadyDisplayed =
      this.lastDisplayedImage === this.displayedImage &&
      _.isEqual(this.lastTransformation, transformations);

    if (isImageAlreadyDisplayed) return;

    this.lastDisplayedImage = this.displayedImage;
    this.lastTransformation = transformations;
    const dataForCanvas = prepareImageForCanvasDisplay(this.displayedImage, transformations);
    if (!dataForCanvas) return;

    this.canvas.width = dataForCanvas.size.width;
    this.canvas.height = dataForCanvas.size.height;

    this.canvas
      .getContext('2d')
      .putImageData(
        new ImageData(dataForCanvas.data, dataForCanvas.size.width, dataForCanvas.size.height),
        0,
        0
      );

    this.dispatchEvent(new DisplayEvent('display_updated', true));
  };

  private displayWithDisplayableImageEncoder = async () => {
    const isImageDisplayable =
      this.displayedImage &&
      this.displayWidth &&
      this.displayHeight &&
      this.element &&
      this.viewport;

    if (!isImageDisplayable) {
      this.dispatchEvent(new DisplayEvent('display_updated', false));
      return;
    }

    const encodeOptions = {
      width: this.displayWidth,
      height: this.displayHeight,
      viewport: this.viewport,
      annotations: this.annotations,
      metadata: this.metadata,
    };

    const isImageAlreadyDisplayed =
      this.lastDisplayedImage === this.displayedImage &&
      _.isEqual(this.lastEncodeOptions, encodeOptions);

    if (isImageAlreadyDisplayed) return;

    const generatedCanvas = await this.displayableImageEncoder.toCanvas(
      this.displayedImage,
      encodeOptions
    );

    if (!generatedCanvas) {
      this.dispatchEvent(new DisplayEvent('display_updated', false));
      return;
    }

    this.lastDisplayedImage = this.displayedImage;
    this.lastEncodeOptions = encodeOptions;

    this.canvas.width = generatedCanvas.width;
    this.canvas.height = generatedCanvas.height;

    this.canvas.getContext('2d').drawImage(generatedCanvas, 0, 0);

    this.dispatchEvent(new DisplayEvent('display_updated', true));
  };

  private triggerCanvasDisplay = () => {
    if (this.displayableImageEncoder) {
      this.displayWithDisplayableImageEncoder();
    } else {
      this.displayWithImageTransformer();
    }
  };

  private updateDimensions = ({ width, height }: Size) => {
    this.displayWidth = Math.floor(width);
    this.displayHeight = Math.floor(height);

    this.triggerCanvasDisplay();
  };

  private handleResize: ResizeObserverCallback = ([resizeData]) => {
    const { width, height } = this.element.getBoundingClientRect();

    this.updateDimensions({ width, height });
  };
}

export const makeThumbnailImageRendererFactory = (): BasicImageRendererFactory => ({
  create: (element: HTMLElement, options?: any) => new ThumbnailImageRenderer(element, options),
});
